/** The search hub's view for every chip (spec §3 “What each chip shows”), from mapped sources. */
import type { TaskStatus } from "@/api/tasks";
import {
  needsYouSection,
  notificationGroupItems,
  notificationSections,
  type NotificationSources,
} from "@/components/panels/notifications/notification-sections";
import type { HubChip } from "@/lib/global-search";
import { translate } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import type en from "@/i18n/messages/panels-search/en";
import { normalizeHubQuery, rankHubResults } from "./hub-ranking";
import type {
  HubEmpty,
  HubGroupId,
  HubItem,
  HubResult,
  HubSection,
  HubView,
} from "./hub-types";

/** Rows each All group shows before “Show all N”. */
export const GROUP_PREVIEW = 3;
/** Opened items All shows under Recent. */
export const RECENT_PREVIEW = 5;
/** Frames the Frames chip lists before typing. */
export const RECENT_FRAMES = 20;
/** The chat browser searches the server from two characters (use-conversation-browser.ts:42). */
const CHAT_SEARCH_MIN = 2;

/** Chips the project filter applies to (spec §3). */
export const PROJECT_SCOPED_CHIPS: ReadonlySet<HubChip> = new Set<HubChip>([
  "all",
  "chats",
  "notifications",
  "tasks",
  "frames",
]);

/** “Show all N” switches to the group's chip; projects and agents have none and expand in place. */
export const GROUP_CHIPS: Readonly<Record<HubGroupId, HubChip | null>> = {
  chats: "chats",
  notifications: "notifications",
  tasks: "tasks",
  frames: "frames",
  memory: "memory",
  settings: "settings",
  projects: null,
  agents: null,
};

const ALL_GROUPS: readonly HubGroupId[] = [
  "chats",
  "notifications",
  "tasks",
  "frames",
  "memory",
  "projects",
  "agents",
  "settings",
];
/** The section-title keys: plain messages without params. */
type SearchKey = {
  [K in keyof typeof en]: (typeof en)[K] extends `${string}{${string}`
    ? never
    : K;
}[keyof typeof en];
/** A section title in the app language, read while the view is built. */
const titleText = (key: SearchKey) => translate(panelsSearchMessages, key);
const GROUP_TITLE_KEYS = {
  chats: "chats",
  notifications: "notifications",
  tasks: "tasks",
  frames: "frames",
  memory: "memory",
  projects: "projects",
  agents: "agents",
  settings: "settings",
} as const satisfies Record<HubGroupId, SearchKey>;
const TASK_SECTIONS: ReadonlyArray<readonly [TaskStatus, SearchKey]> = [
  ["blocked", "taskBlocked"],
  ["in_progress", "taskInProgress"],
  ["in_review", "taskInReview"],
  ["todo", "taskTodo"],
  ["backlog", "taskBacklog"],
  ["done", "taskDone"],
];

export interface HubSources extends NotificationSources {
  notificationLoading?: boolean;
  notificationError?: string | null;
  searchError?: string | null;
  memoryLoading?: boolean;
  memoryError?: string | null;
  chats: {
    active: HubResult[];
    archived: HubResult[];
    archivedCount: string;
    loading: boolean;
    error: string | null;
    hasMore: boolean;
    archivedHasMore: boolean;
  };
  tasks: { results: HubResult[]; total: number; loading: boolean };
  frames: { results: HubResult[]; total: number; loading: boolean };
  agents: HubResult[];
  memory: HubResult[];
  projects: HubResult[];
  settings: HubResult[];
  recent: HubResult[];
}

export interface HubViewState {
  chip: HubChip;
  query: string;
  projectId: string | null;
  projectName: string | null;
  archivedExpanded: boolean;
  expandedGroups: ReadonlySet<HubGroupId>;
  now: number;
}

interface ViewOptions {
  loading?: boolean;
  error?: string | null;
  loadMoreChats?: boolean;
  loadMoreArchived?: boolean;
}

function resultItem(result: HubResult): HubItem {
  return { type: "result", key: `${result.kind}-${result.id}`, result };
}

function inProject(
  results: readonly HubResult[],
  projectId: string | null,
): HubResult[] {
  return projectId
    ? results.filter((result) => result.projectId === projectId)
    : [...results];
}

function startOfLocalDay(now: number): number {
  const date = new Date(now);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
}

function sectionOf(
  id: string,
  title: string | null,
  items: HubItem[],
): HubSection[] {
  return items.length > 0 ? [{ id, title, items }] : [];
}

/** Server-matched rows (chat search, workspace search) stay even when the words match elsewhere. */
function chatRanking(query: string) {
  return { keepUnmatched: normalizeHubQuery(query).length >= CHAT_SEARCH_MIN };
}

function emptyFor(state: HubViewState, error: string | null): HubEmpty | null {
  if (error) return { kind: "error", message: error };
  const query = state.query.trim();
  if (query) {
    const narrowed =
      PROJECT_SCOPED_CHIPS.has(state.chip) && state.projectId !== null;
    return {
      kind: "no-match",
      query,
      projectName: narrowed ? state.projectName : null,
    };
  }
  if (state.chip === "notifications") return { kind: "caught-up" };
  if (state.chip === "memory") return { kind: "search-memory" };
  return state.chip === "all" ? null : { kind: "nothing-yet" };
}

function view(
  sections: HubSection[],
  state: HubViewState,
  options: ViewOptions = {},
): HubView {
  const hasItems = sections.some((section) => section.items.length > 0);
  const loading = Boolean(options.loading) && !hasItems;
  return {
    sections,
    empty: hasItems || loading ? null : emptyFor(state, options.error ?? null),
    loading,
    loadMoreChats: options.loadMoreChats ?? false,
    loadMoreArchived: options.loadMoreArchived ?? false,
  };
}

function allView(sources: HubSources, state: HubViewState): HubView {
  if (!normalizeHubQuery(state.query)) {
    const needsYou = needsYouSection(sources.approvals, "");
    const recent = inProject(sources.recent, state.projectId)
      .slice(0, RECENT_PREVIEW)
      .map(resultItem);
    return view(
      [
        ...(needsYou ? [needsYou] : []),
        ...sectionOf("recent", titleText("recent"), recent),
      ],
      state,
    );
  }
  const serverMatched = { keepUnmatched: true };
  const groups: Record<HubGroupId, HubItem[]> = {
    chats: rankHubResults(
      inProject(sources.chats.active, state.projectId),
      state.query,
      chatRanking(state.query),
    ).map(resultItem),
    notifications: notificationGroupItems(sources, {
      query: state.query,
      projectId: state.projectId,
    }),
    tasks: rankHubResults(
      inProject(sources.tasks.results, state.projectId),
      state.query,
      serverMatched,
    ).map(resultItem),
    frames: rankHubResults(
      inProject(sources.frames.results, state.projectId),
      state.query,
      serverMatched,
    ).map(resultItem),
    memory: rankHubResults(sources.memory, state.query, serverMatched).map(
      resultItem,
    ),
    projects: rankHubResults(sources.projects, state.query).map(resultItem),
    agents: rankHubResults(
      inProject(sources.agents, state.projectId),
      state.query,
      serverMatched,
    ).map(resultItem),
    settings: rankHubResults(sources.settings, state.query).map(resultItem),
  };
  const totals: Partial<Record<HubGroupId, number>> = {
    tasks: sources.tasks.total,
    frames: sources.frames.total,
  };
  const sections = ALL_GROUPS.flatMap((group): HubSection[] => {
    const items = groups[group];
    if (items.length === 0) return [];
    const total = Math.max(totals[group] ?? 0, items.length);
    const expanded =
      GROUP_CHIPS[group] === null && state.expandedGroups.has(group);
    const shown = expanded ? items : items.slice(0, GROUP_PREVIEW);
    const more: HubItem[] =
      !expanded && total > GROUP_PREVIEW
        ? [{ type: "show-all", key: `show-all-${group}`, group, count: total }]
        : [];
    return [
      {
        id: group,
        title: titleText(GROUP_TITLE_KEYS[group]),
        items: [...shown, ...more],
      },
    ];
  });
  return view(sections, state, {
    loading:
      sources.chats.loading || sources.tasks.loading || sources.frames.loading,
    error:
      sources.searchError ?? sources.chats.error ?? sources.notificationError,
  });
}

/**
 * Active chats in the project (all with `projectId` null), ranked for the
 * query, as Running, Today and Earlier: the hub's Chats chip and the wall's
 * Chats pin.
 */
export function chatSections(
  chats: readonly HubResult[],
  query: string,
  projectId: string | null,
  now: number,
): HubSection[] {
  const active = rankHubResults(
    inProject(chats, projectId),
    query,
    chatRanking(query),
  );
  const today = startOfLocalDay(now);
  const isRunning = (result: HubResult) =>
    result.kind === "chat" && result.running;
  const idle = active.filter((result) => !isRunning(result));
  return [
    ...sectionOf(
      "running",
      titleText("running"),
      active.filter(isRunning).map(resultItem),
    ),
    ...sectionOf(
      "today",
      titleText("today"),
      idle.filter((result) => result.time >= today).map(resultItem),
    ),
    ...sectionOf(
      "earlier",
      titleText("earlier"),
      idle.filter((result) => result.time < today).map(resultItem),
    ),
  ];
}

function chatsView(sources: HubSources, state: HubViewState): HubView {
  const { chats } = sources;
  const sections = chatSections(
    chats.active,
    state.query,
    state.projectId,
    state.now,
  );
  const archived = rankHubResults(
    inProject(chats.archived, state.projectId),
    state.query,
    chatRanking(state.query),
  );
  if (archived.length > 0 || chats.archivedHasMore) {
    sections.push({
      id: "archived",
      title: null,
      items: [
        {
          type: "archived",
          key: "archived-chats",
          count: chats.archivedCount,
          expanded: state.archivedExpanded,
        },
        ...(state.archivedExpanded ? archived.map(resultItem) : []),
      ],
    });
  }
  return view(sections, state, {
    loading: chats.loading,
    error: chats.error,
    loadMoreChats: chats.hasMore,
    loadMoreArchived: state.archivedExpanded && chats.archivedHasMore,
  });
}

function tasksView(sources: HubSources, state: HubViewState): HubView {
  const results = rankHubResults(
    inProject(sources.tasks.results, state.projectId),
    state.query,
    { keepUnmatched: true },
  );
  const sections = TASK_SECTIONS.flatMap(([status, key]) =>
    sectionOf(
      `tasks-${status}`,
      titleText(key),
      results
        .filter((result) => result.kind === "task" && result.status === status)
        .map(resultItem),
    ),
  );
  return view(sections, state, {
    loading: sources.tasks.loading,
    error: sources.searchError,
  });
}

function framesView(sources: HubSources, state: HubViewState): HubView {
  if (normalizeHubQuery(state.query)) {
    const results = rankHubResults(
      inProject(sources.frames.results, state.projectId),
      state.query,
      { keepUnmatched: true },
    );
    return view(
      sectionOf("results", titleText("results"), results.map(resultItem)),
      state,
      { loading: sources.frames.loading, error: sources.searchError },
    );
  }
  const opened = inProject(
    sources.recent.filter((result) => result.kind === "frame"),
    state.projectId,
  );
  const seen = new Set(opened.map((result) => result.id));
  const latest = inProject(sources.frames.results, state.projectId).filter(
    (result) => !seen.has(result.id),
  );
  const items = [...opened, ...latest].slice(0, RECENT_FRAMES).map(resultItem);
  return view(sectionOf("recent", titleText("recent"), items), state, {
    loading: sources.frames.loading,
    error: sources.searchError,
  });
}

function listView(
  id: "settings",
  results: readonly HubResult[],
  state: HubViewState,
  sectionTitle: string,
): HubView {
  const typing = normalizeHubQuery(state.query).length > 0;
  const items = (
    typing ? rankHubResults(results, state.query) : [...results]
  ).map(resultItem);
  return view(
    sectionOf(id, typing ? titleText("results") : sectionTitle, items),
    state,
  );
}

export function buildHubView(
  sources: HubSources,
  state: HubViewState,
): HubView {
  switch (state.chip) {
    case "all":
      return allView(sources, state);
    case "chats":
      return chatsView(sources, state);
    case "notifications":
      return view(
        notificationSections(sources, {
          query: state.query,
          projectId: state.projectId,
          now: state.now,
        }),
        state,
        {
          loading: sources.notificationLoading,
          error: sources.notificationError,
        },
      );
    case "tasks":
      return tasksView(sources, state);
    case "frames":
      return framesView(sources, state);
    case "memory":
      return view(
        sectionOf(
          "memory",
          titleText("results"),
          rankHubResults(sources.memory, state.query, {
            keepUnmatched: true,
          }).map(resultItem),
        ),
        state,
        { loading: sources.memoryLoading, error: sources.memoryError },
      );
    case "settings":
      return listView(
        "settings",
        sources.settings,
        state,
        titleText("settings"),
      );
  }
}
